import { Inject, Injectable } from "@angular/core";
import { Environment } from "../classes/environment";
import { User } from "../classes/user";
import { catchError, map, Observable, of } from "rxjs";
import { Chat, ChatBackend, ChatDateien, ChatFile, ChatGroup, ChatGroupBackend, ChatgruppeBenutzer, ChatNachrichten, ChatType, Message } from "../classes/chat";
import { ChatPrototypeService } from "./chat-prototype.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { CalendarGroup } from "../classes/calendar";
import { formatDate } from "@angular/common";
import { CurrentUserService } from "./current-user.service";


@Injectable( { providedIn: 'root' } )
export class ChatService {


	constructor(
		@Inject( "environment" ) protected environment: Environment,
		private chatPrototypeService: ChatPrototypeService,
		private httpClient: HttpClient,
		@Inject('LOCALSTORAGE') private localStorage: Storage,
		private currentUserService: CurrentUserService
	) {}


	acceptChatConditions(): Observable<boolean> {

		return this.httpClient.put<{ verarbeitet: boolean, benutzer: User }> (
			this.environment.backendUrl + 'benutzer/chat/bestaetigen',
			{}
		).pipe(
			map( result => {

				if( !result || !result.verarbeitet ) return false;

				const user = this.currentUserService.currentUser();
				if( !user ) return false;

				user.ebeChatNutzungsbedingungBestaetigt = true;
				this.localStorage.setItem( 'currentUser', JSON.stringify( user ) );
				return true;

			}),
			catchError( e => of(false) ),
		);

	}

	getContacts(): Observable<User[] | null> {

		if (this.environment.chatPrototype) {
			return this.chatPrototypeService.getContacts();
		}
		
		return this.httpClient.get<User[]>(`${this.environment.backendUrl}benutzer/chat/all`).pipe(
			map( users => users || null),
			catchError((error) => {
				console.error( error);
				return of(null);
			})
		);
	}	

	getChats( page=0 ): Observable<{ chats: Chat[], bMoreResults: boolean }|null> {

		return this.httpClient.get<{ content: ChatBackend[], totalPages: number }>(
			this.environment.backendUrl + 'chat/page',
			{
				params: new HttpParams()
					.set( "page", page )
					.set( "size", 20 )
			}
		).pipe(
			map( pageChat => {

				if( !pageChat || !pageChat.content ) return null;

				return {
					chats: pageChat.content
						.map( c => this.convertChatBackendToChat( c ) ),
					bMoreResults: (pageChat.totalPages > (page+1))
				};

			}),
			catchError( e => of(null) ),
		);

	}

	searchChats( searchTxt: string, page=0 ): Observable<{ chats: Chat[], bMoreResults: boolean }|null> {

		return this.httpClient.get<{ content: ChatBackend[], totalPages: number }>(
			this.environment.backendUrl + 'chat/search',
			{
				params: new HttpParams()
					.set( "text", searchTxt )
					.set( "page", page )
					.set( "size", 10 )
			}
		).pipe(
			map( pageChat => {

				if( !pageChat || !pageChat.content ) return null;

				return {
					chats: pageChat.content
						.map( c => this.convertChatBackendToChat( c ) ),
					bMoreResults: (pageChat.totalPages > (page+1))
				};

			}),
			catchError( e => of(null) ),
		);

	}

	getChat( chatId: string ): Observable<Chat|null> {

		return this.httpClient.get<ChatBackend>( this.environment.backendUrl + 'chat/' + chatId ).pipe(
			map( chatBackend => {

				if( !chatBackend ) return null;

				return this.convertChatBackendToChat( chatBackend );

			}),
			catchError( e => of(null) ),
		);
		
	}

	createChat( chat: Chat ): Observable<Chat|null> {

		return this.httpClient.post<ChatBackend>(
			this.environment.backendUrl + 'chat/new',
			this.convertChatToChatBackend( chat )
		).pipe(
			map( chatBackend => {
				if( !chatBackend ) return null;
				return this.convertChatBackendToChat( chatBackend );
			} ),
			catchError( e => of(null) )
		);

	}

	private convertChatToChatBackend( chat: Chat ): ChatBackend {

		const chatBackend = new ChatBackend();
		chatBackend.chId = chat.id;
		chatBackend.title = chat.title;
		chatBackend.chbErinnerungsmail = chat.bNotifications;

		if( chat.chatType == ChatType.direct ) {
			chatBackend.empfaengerEbeId = chat.sourceId;
		}
		else if( chat.chatType == ChatType.userGroup ) {
			chatBackend.cgId = chat.sourceId;
		}
		else if( chat.chatType == ChatType.systemGroup ) {
			chatBackend.vgId = chat.sourceId;
		}

		return chatBackend;

	}

	private convertChatBackendToChat( chatBackend: ChatBackend ): Chat {

		const chat = new Chat();
		chat.id = chatBackend.chId;
		chat.title = chatBackend.title;
		chat.lastMsg = chatBackend.lastMessagePreview;
		if( chatBackend.chLetzteNachricht !== null ) {
			chat.lastMsgTms = chatBackend.chLetzteNachricht;
		}
		if(  ( chatBackend.chLetzteNachricht !== null ) && ( chatBackend.zuletztGesehen !== null ) ) {
			chat.bNewMsg = ( chatBackend.chLetzteNachricht > chatBackend.zuletztGesehen );
		}
		chat.bNotifications = !!chatBackend.chbErinnerungsmail;

		if( chatBackend.empfaengerEbeId ) {
			chat.chatType = ChatType.direct;
			chat.sourceId = chatBackend.empfaengerEbeId;
		}
		else if( chatBackend.cgId ) {
			chat.chatType = ChatType.userGroup;
			chat.sourceId = chatBackend.cgId;
		}
		else if( chatBackend.vgId ) {
			chat.chatType = ChatType.systemGroup;
			chat.sourceId = chatBackend.vgId;
		}

		if( chatBackend.nachrichten ) {
			chat.messages = chatBackend.nachrichten.content.map( n => this.convertChatNachrichtenToMessage(n) );
			if( chat.messages.length ) {
				chat.lastMsgTmsStr = this.convertTmsToStr( chat.messages[ chat.messages.length-1 ].timestamp );
			}
		}

		return chat;

	}

	private convertTmsToStr( tms: number|null ): string {

		if( tms === null ) return "";
		if( tms === undefined ) return "";

		const dt = new Date();
		dt.setTime( tms );
		dt.setHours( 0, 0, 0, 0 );
		const dtNow = new Date();
		dtNow.setHours( 0, 0, 0, 0 );
		if( dt.getTime() == dtNow.getTime() ) return formatDate( tms, "HH:mm", "de-DE" );

		return formatDate( tms, "dd/MM", "de-DE" );

	}
	
	private convertChatNachrichtenToMessage( chatNachrichten: ChatNachrichten ): Message {
		
		const message = new Message();
		message.id = chatNachrichten.cnId;
		message.text = chatNachrichten.text || "";
		message.textPerLine = message.text.split( "\n" );
		message.timestamp = chatNachrichten.anlageDatum;
		message.senderId = chatNachrichten.absenderId;
		message.chatId = chatNachrichten.chId;
		message.msgRepliedToId = chatNachrichten.originalCnId;
		if( chatNachrichten.originalNachricht ) {
			message.msgRepliedToTxt = chatNachrichten.originalNachricht.text;
			message.msgRepliedToSenderId = chatNachrichten.originalNachricht.absenderId;
		}

		if( chatNachrichten.dateien ) {
			message.files = chatNachrichten.dateien.map( cn => this.convertChatDateienToChatFile(cn) )
		}

		return message;

	}

	private convertMessageToChatNachrichten( message: Message ): ChatNachrichten {
		
		const chatNachrichten = new ChatNachrichten();
		chatNachrichten.chId = message.chatId;
		chatNachrichten.text = message.text;
		chatNachrichten.absenderId = message.senderId;
		chatNachrichten.originalCnId = message.msgRepliedToId;

		return chatNachrichten;

	}

	private convertChatDateienToChatFile( chatDateien: ChatDateien ): ChatFile {

		const chatFile = new ChatFile();
		chatFile.id = chatDateien.dateiId;
		chatFile.fileName = chatDateien.dateiName;
		return chatFile;

	}

	sendMessage( message: Message, files: ChatFile[] ): Observable<Message|null|413> {

		const chatNachrichten = this.convertMessageToChatNachrichten( message );
		const o = {
			chId: chatNachrichten.chId,
			text: chatNachrichten.text,
			originalCnId: chatNachrichten.originalCnId || null
		};

		const formData = new FormData();
		formData.append( "nachricht", new Blob( [JSON.stringify( o )], { type: 'application/json' }) );

		for( const file of files ) {
			if( file.file ) {
				formData.append( 'files', file.file, file.fileName );
			}
		}

		return this.httpClient.post<ChatNachrichten>(
			this.environment.backendUrl + 'chat/messages/new',
			formData,
		).pipe(
			map( result => {

				if( !result || !result.cnId ) {
					return null;
				}

				const message = this.convertChatNachrichtenToMessage( result );
				message.timestamp = (new Date()).getTime();
				return message;

			}),
			catchError( (error, src) => {
					if( error && error.status && (error.status == 413) ) return of(413 as 413);
					return of(null);
			})
		);
		
	}

	getMessages( chatId: string, page=0 ): Observable<Message[]|null> {

		return this.httpClient.get<{ content: ChatNachrichten[] }>(
			this.environment.backendUrl + 'chat/messages',
			{
				params: new HttpParams()
					.set( "chId", chatId )
					.set( "page", page )
					.set( "size", 20 )
			}
		).pipe(
			map( pageChatMessage => {

				if( !pageChatMessage || !pageChatMessage.content ) return null;

				return pageChatMessage.content
					.map( c => this.convertChatNachrichtenToMessage( c ) );

			}),
			catchError( e => of(null) ),
		);

	}

	private convertCalendarGroupToChatGroup( calendarGroup: CalendarGroup ): ChatGroup {
		const chatGroup = new ChatGroup();
		chatGroup.id = calendarGroup.vgId;
		chatGroup.title = calendarGroup.vgName;
		chatGroup.bSystem = true;
		return chatGroup;
	}

	private convertChatGroupBackendToChatGroup( chatGroupBackend: ChatGroupBackend ): ChatGroup {
		const chatGroup = new ChatGroup();
		chatGroup.id = chatGroupBackend.cgId;
		chatGroup.title = chatGroupBackend.cgName;
		if( chatGroupBackend.chatgruppeBenutzern ) {
			chatGroup.users = chatGroupBackend.chatgruppeBenutzern.map( cgb => {
				return cgb.ebeId || cgb.cgbId;
			});
			const user = this.currentUserService.currentUser();
			if( user ) {
				chatGroup.bAdmin = !!chatGroupBackend.chatgruppeBenutzern.find( cgb => (cgb.ebeId == user.ebeId) && cgb.cgAdmin );
			}
		}
		chatGroup.bSystem = false;
		return chatGroup;
	}

	getChatGroup( chatGroupId: string ): Observable<ChatGroup|null> {

		return this.httpClient.get<ChatGroupBackend>( this.environment.backendUrl + 'chatgruppe/' + chatGroupId ).pipe(
			map( chatGroupBackend => {

				if( !chatGroupBackend ) return null;

				return this.convertChatGroupBackendToChatGroup( chatGroupBackend );

			}),
			catchError( e => of(null) ),
		);

	}

	editChatGroup( chatGroup: ChatGroup ): Observable<ChatGroup|null> {

		if( this.environment.chatPrototype ) return this.chatPrototypeService.editChatGroup( chatGroup );

		return this.httpClient.put<ChatGroupBackend>(
			this.environment.backendUrl + 'chatgruppe/update',
			this.convertChatGroupToChatGroupBackend( chatGroup )
		).pipe(
			map( chatGroupBackend => {

				if( !chatGroupBackend ) return null;

				return this.convertChatGroupBackendToChatGroup( chatGroupBackend );

			}),
			catchError( e => of(null) ),
		);

	}
	
	addUsersToChatGroup( chatGroupId: string, userIds: string[] ): Observable<boolean> {

		const chatGroup = new ChatGroup();
		chatGroup.id = chatGroupId;
		chatGroup.users = userIds;

		return this.httpClient.put<ChatGroupBackend> (
			this.environment.backendUrl + 'chatgruppe/benutzer/add',
			this.convertChatGroupToChatGroupBackend( chatGroup )
		).pipe(
			map( result => {

				if( !result || !result.cgId ) return false;

				return true;

			}),
			catchError( e => of(false) ),
		);

	}

	removeUsersFromChatGroup( chatGroupId: string, userIds: string[] ): Observable<boolean> {

		const chatGroup = new ChatGroup();
		chatGroup.id = chatGroupId;
		chatGroup.users = userIds;

		return this.httpClient.delete<ChatGroupBackend> (
			this.environment.backendUrl + 'chatgruppe/benutzer/remove',
			{ body: this.convertChatGroupToChatGroupBackend( chatGroup ) }
		).pipe(
			map( result => {

				if( !result || !result.cgId ) return false;

				return true;

			}),
			catchError( e => of(false) ),
		);

	}

	createChatGroup( chatGroup: ChatGroup ): Observable<ChatGroup|null> {

		return this.httpClient.post<ChatGroupBackend>(
			this.environment.backendUrl + 'chatgruppe/new',
			this.convertChatGroupToChatGroupBackend( chatGroup )
		).pipe(
			map( cgb => {

				if( !cgb ) return null;

				return this.convertChatGroupBackendToChatGroup( cgb );

			}),
			catchError( e => of(null) )
		);

	}

	private convertChatGroupToChatGroupBackend( chatGroup: ChatGroup ): ChatGroupBackend {

		const chatGroupBackend = new ChatGroupBackend();
		chatGroupBackend.cgId = chatGroup.id;
		chatGroupBackend.cgName = chatGroup.title;
		chatGroupBackend.chatgruppeBenutzern = chatGroup.users.map( userId => {
			const cgB = new ChatgruppeBenutzer();
			cgB.ebeId = userId;
			return cgB;
		});

		return chatGroupBackend;

	}

	getSystemChatGroupUsers( systemGroupId: string ): Observable<string[]|null> {

		return this.httpClient.get<{ ebeId: string }[]>(
			this.environment.backendUrl + 'eventGroups/members/' + systemGroupId
		).pipe(
			map( users => {

				if( !users ) return null;

				return users.map( u => u.ebeId );

			}),
			catchError( e => of(null) ),
		);

	}

	setChatNotification( chatId: string, b: boolean ): Observable<boolean> {

		return this.httpClient.put<{ chbErinnerungsmail: boolean }> (
			this.environment.backendUrl + 'chat/notifications',
			{
				chId: chatId,
				chbErinnerungsmail: b
			}
		).pipe(
			map( result => {

				if( !result || (result.chbErinnerungsmail != b)  ) return false;

				return true;

			}),
			catchError( e => of(false) ),
		);

	}

}

